import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import RecoveryPanel from "../components/RecoveryPanel";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import EmptyState from "../components/ui/EmptyState";
import LoadingState from "../components/ui/LoadingState";
import {
  fetchRecommendationHistory,
  fetchSavedShows,
  removeSavedShow,
} from "../api/client";

function showTitle(item) {
  return item?.show?.title || item?.title || "Unavailable title";
}

export default function SavedShows() {
  const location = useLocation();
  const [savedState, setSavedState] = useState({
    loading: true,
    error: "",
    saved: [],
    history: [],
  });
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState("");

  const loadLibrary = async () => {
    setSavedState((current) => ({ ...current, loading: true, error: "" }));
    setRemoveError("");

    try {
      const [savedResponse, historyResponse] = await Promise.all([
        fetchSavedShows(),
        fetchRecommendationHistory(),
      ]);

      setSavedState({
        loading: false,
        error: "",
        saved: savedResponse.data?.results || [],
        history: historyResponse.data?.recommendation_history || [],
      });
    } catch (error) {
      setSavedState({
        loading: false,
        error: "We could not load your saved shows. Please sign in again.",
        saved: [],
        history: [],
      });
    }
  };

  useEffect(() => {
    loadLibrary();
  }, []);

  const handleRemove = async (showId) => {
    try {
      setRemovingId(showId);
      setRemoveError("");
      const response = await removeSavedShow(showId);
      setSavedState((current) => ({
        ...current,
        saved: response.data?.results || [],
      }));
    } catch {
      setRemoveError("We could not remove that show. Please try again.");
    } finally {
      setRemovingId(null);
    }
  };

  if (savedState.loading) {
    return (
      <div className="px-4 py-10">
        <LoadingState title="Loading your saved shows" message="Opening your personal watchlist." />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-4xl font-bold text-hookd-cream">Saved Shows</h1>
          <p className="mt-2 text-sm text-hookd-muted">Your watchlist and recent recommendation sessions.</p>
        </div>
        <Link to="/quiz" className="inline-flex min-h-11 items-center text-hookd-empress font-semibold underline">
          Get more recommendations
        </Link>
      </div>

      {location.state?.convertedGuest && (
        <div className="rounded-hookd border border-green-400/35 bg-green-400/10 p-4 text-green-200">
          Your guest quiz and recommendation history were saved to this account.
        </div>
      )}

      {savedState.error && (
        <RecoveryPanel
          title="Saved shows did not load"
          message={savedState.error}
          actions={[
            {
              label: "Retry",
              onClick: loadLibrary,
            },
            {
              label: "Sign In",
              to: "/login",
            },
          ]}
        />
      )}

      {!savedState.error && (
        <>
          {removeError && (
            <RecoveryPanel
              title="Show was not removed"
              message={removeError}
              actions={[
                {
                  label: "Retry Load",
                  onClick: loadLibrary,
                },
              ]}
            />
          )}

          <Card className="p-5">
            <h2 className="mb-4 font-display text-3xl font-bold text-hookd-cream">Watchlist</h2>
            {savedState.saved.length === 0 ? (
              <EmptyState
                title="No saved shows yet"
                message="Save a recommendation when one hits the right vibe."
                primaryAction={{ label: "Get Recommendations", to: "/quiz" }}
              />
            ) : (
              <div className="grid gap-3">
                {savedState.saved.map((item) => (
                  <div
                    key={item.show_id}
                    className="flex flex-col gap-3 rounded-hookd border border-hookd-line bg-white/5 p-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <h3 className="font-display text-2xl font-bold break-words text-hookd-cream">{showTitle(item)}</h3>
                      {item.unavailable ? (
                        <p className="text-sm text-red-300">This title is no longer available in the catalog.</p>
                      ) : (
                        <p className="text-sm text-hookd-muted break-words">
                          {item.show?.platform || "Platform unknown"} - {(item.show?.genres || []).join(", ") || "Genres not set"}
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleRemove(item.show_id)}
                      loading={removingId === item.show_id}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      {removingId === item.show_id ? "Removing..." : "Remove"}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card className="p-5">
            <h2 className="mb-4 font-display text-3xl font-bold text-hookd-cream">Recommendation History</h2>
            {savedState.history.length === 0 ? (
              <p className="text-hookd-muted">No saved recommendation sessions yet.</p>
            ) : (
              <div className="space-y-4">
                {savedState.history.map((session) => (
                  <div key={session.session_id} className="rounded-hookd border border-hookd-line bg-white/5 p-4">
                    <p className="text-sm font-semibold text-hookd-gold mb-2">
                      {session.created_at ? new Date(session.created_at).toLocaleString() : "Saved session"}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(session.results || []).map((item, index) => (
                        <div key={`${session.session_id}-${index}`} className="rounded-hookd bg-hookd-ink/70 p-3">
                          <p className="font-semibold break-words text-hookd-cream">{showTitle(item)}</p>
                          {item.unavailable ? (
                            <p className="text-xs text-red-300">No longer available in the catalog.</p>
                          ) : (
                            <p className="text-xs text-hookd-muted">
                              {item.show?.platform || "Platform unknown"}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
